import * as React from 'react';
import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import { withTheme } from 'styled-components';
import { Table, TableBody, TableRow } from './Grid';
import DownloadControls from './DownloadControls';
import WatchListToggle from './WatchListToggle';
import DocumentDropDown from './DocumentDropDown';
import ChangeIndicator from './ChangeIndicator';
import StarRating from './StarRating';
import { ShareclassColumn } from './ShareclassRows';
import Icon from './Icon';
import Translate from './Translate';
import DynamicLink from './DynamicLink';
import { CellDisclaimer } from '../models/configuration/explorer/CellDisclaimer';
import { DataStore } from '../stores';
import DocumentDropDownDownloadControls from './DocumentDropDownDownloadControls';

const starIconStyleProps = {
    height: '1.2em',
    width: '1.2em',
};

@inject('dataStore')
@observer
class ShareclassColumnValue extends Component<any> {
    @computed
    get selectedGroupIndex() {
        let index = this.props.selectedGroupMap.get(this.props.columnIndex);
        if (this.props.column.groups) {
            if (index >= this.props.column.groups.length) {
                const selectedConfigGroupIndex = this.props.column.groups.findIndex(
                    c => c.selected
                );
                // tslint:disable-next-line: prefer-conditional-expression
                if (selectedConfigGroupIndex > -1) {
                    index = selectedConfigGroupIndex;
                } else {
                    index = 0;
                }
            }
        }
        return index;
    }

    @computed
    get column() {
        return this.props.column.groups
            ? this.props.column.groups[this.selectedGroupIndex]
            : this.props.column;
    }
    getGroupOrDropdownColumnContent = column => {
        const { theme, isShareclassDisabled = false } = this.props;
        const shareclassColumnGroupedTheme = isShareclassDisabled
            ? theme.ShareclassColumnGroupedDisabled
            : theme.ShareclassColumnGrouped;

        return (
            <Table>
                <TableBody>
                    <TableRow>
                        {column.values &&
                            column.values.map((childColumn, cIndex) => {
                                return (
                                    <ShareclassColumn
                                        key={`${column.type}-${childColumn.value}-${cIndex}`}
                                        paddingX="sm"
                                        paddingY="0"
                                        textAlign={
                                            childColumn.textAlign || 'center'
                                        }
                                        width={childColumn.width}
                                        whiteSpace={
                                            childColumn.wrapText
                                                ? 'normal'
                                                : 'nowrap'
                                        }
                                        {...shareclassColumnGroupedTheme}
                                    >
                                        {this.getColumnContent(childColumn)}
                                    </ShareclassColumn>
                                );
                            })}
                    </TableRow>
                </TableBody>
            </Table>
        );
    };
    getDownloadColumnContent = column => {
        const { theme, dataStore, shareClass } = this.props;
        return (
            <DownloadControls
                count={shareClass.downloadCount}
                display="inline-flex"
                disabled={shareClass.downloadCount === 0}
                theme={{
                    ...theme,
                    ...{
                        DownloadButton: theme.ShareclassRowDownloadButton,
                        DownloadButtonIcon:
                            theme.ShareclassRowDownloadButtonIcon,
                        DownloadCount: theme.ShareclassRowDownloadCount,
                    },
                }}
                onClick={e => {
                    e.stopPropagation();
                    dataStore.downloadDocuments(shareClass.shareClassDownloads);
                }}
            />
        );
    };
    getWatchlistColumnContent = column => {
        const { shareClass } = this.props;
        return (
            <WatchListToggle
                onClick={e => {
                    e.stopPropagation();
                    shareClass.toggleWatchList();
                }}
                active={shareClass.inWatchList}
                accessibilityContext={`for share class identified by ${shareClass.shareClass.clientCode}`}
            />
        );
    };
    getDocumentColumnContent = column => {
        const { theme, dataStore, shareClass } = this.props;
        const documents = column.value;

        if (Array.isArray(column.value) && documents.length > 0) {
            if (column.showSingleDocument && documents.length === 1) {
                const showLabel = column.showSingleDocumentLabel ?? false;
                return (
                    <DocumentDropDownDownloadControls
                        document={documents[0]}
                        documentSelected={shareClass.documentSelected}
                        toggleDownload={shareClass.toggleDownload}
                        showLabel={showLabel}
                    />
                );
            } else {
                return (
                    <DocumentDropDown
                        documents={column.value}
                        documentSelected={shareClass.documentSelected}
                        toggleDownload={shareClass.toggleDownload}
                    >
                        <Icon
                            fileName="DocumentIcon"
                            height="1.4em"
                            width="1.4em"
                            {...theme.ShareclassRowDocumentIcon}
                        />
                    </DocumentDropDown>
                );
            }
        }
        return (
            <Translate>
                {column.noDataPlaceholder || dataStore.config.noDataPlaceholder}
            </Translate>
        );
    };
    getPricesColumnContent = column => {
        const { dataStore, shareClass } = this.props;
        if (column.data.performanceCheck && !shareClass.hasPerformance) {
            return <Translate>{column.data.value}</Translate>;
        } else {
            return (
                <DownloadControls
                    display="inline-flex"
                    onClick={e => {
                        e.stopPropagation();
                        dataStore.getPriceHistory(
                            column,
                            shareClass.shareClass.clientCode
                        );
                    }}
                    count={1}
                    showCount={false}
                />
            );
        }
    };
    getStarsColumnContent = column => {
        const { theme, shareClass } = this.props;
        if (column.data.performanceCheck && !shareClass.hasPerformance) {
            return <Translate>{column.data.value}</Translate>;
        } else {
            return (
                <StarRating
                    rating={column.data.value}
                    noDataPlaceholder={column.noDataPlaceholder}
                    showEmptyStars={column.type === 'starsOutOf'}
                    display="block"
                    theme={{
                        ...theme,
                        ...theme.StarRatingColumnWrapper,
                        StarIcon: {
                            ...starIconStyleProps,
                            ...theme.StarIconColumn,
                        },
                        StarOutlineIcon: {
                            ...starIconStyleProps,
                            ...theme.StarOutlineIconColumn,
                        },
                    }}
                />
            );
        }
    };
    getSimpleColumnContent = (column, data, key: any = undefined) => {
        const { dataStore } = this.props;
        const separator = column.multiple && data.separator;
        return (
            <Fragment key={key}>
                {data.showChange ? (
                    <ChangeIndicator
                        dataStore={dataStore}
                        showChange={data.showChange}
                        value={data.value}
                        separator={separator}
                    />
                ) : (
                    <Fragment>
                        <Translate>{data.value}</Translate>
                        {separator || null}
                    </Fragment>
                )}
            </Fragment>
        );
    };
    loadDisclaimerSuperscripts = (column, columnContent) => {
        const { dataStore, shareClass: shareClassWrapper } = this.props;
        const entity = shareClassWrapper.shareClass;
        if (column.cellDisclaimers && column.cellDisclaimers.length > 0) {
            const cellDisclaimers = column.cellDisclaimers as Array<
                CellDisclaimer
            >;
            const disclaimerStore = (dataStore as DataStore).disclaimerStore;
            if (disclaimerStore) {
                const superscripts = disclaimerStore.getSuperscripts(
                    entity,
                    cellDisclaimers
                );
                if (superscripts && superscripts.length > 0) {
                    return (
                        <Fragment>
                            {columnContent}
                            <sup>{superscripts.join(', ')}</sup>
                        </Fragment>
                    );
                }
            }
        }
        return columnContent;
    };
    getColumnContent = column => {
        const { theme, dataStore, shareClass, queryContext } = this.props;

        let columnContent;
        // Determine which column types can be wrapper with an anchor
        let hasLink = false;
        if (['group', 'dropdown'].includes(column.type)) {
            hasLink = true;
            columnContent = this.getGroupOrDropdownColumnContent(column);
        } else if (column.type === 'download') {
            columnContent = this.getDownloadColumnContent(column);
        } else if (column.type === 'watchlist' && dataStore.watchListEnabled) {
            columnContent = this.getWatchlistColumnContent(column);
        } else if (column.type === 'document') {
            columnContent = this.getDocumentColumnContent(column);
        } else if (column.type === 'prices') {
            columnContent = this.getPricesColumnContent(column);
        } else if (column.type === 'stars' || column.type === 'starsOutOf') {
            columnContent = this.getStarsColumnContent(column);
        } else if (column.multiple) {
            hasLink = true;
            columnContent = (
                <Fragment>
                    {column.data.map((data, i) =>
                        this.getSimpleColumnContent(column, data, i)
                    )}
                </Fragment>
            );
        } else {
            hasLink = true;
            columnContent = this.getSimpleColumnContent(column, column.data);
        }

        columnContent = this.loadDisclaimerSuperscripts(column, columnContent);

        if (hasLink && column.url) {
            return (
                <DynamicLink
                    config={column}
                    shareClass={shareClass.shareClass}
                    dataStore={dataStore}
                    queryContext={queryContext}
                    {...theme.ShareclassColumnLink}
                >
                    {columnContent}
                </DynamicLink>
            );
        }

        return columnContent;
    };
    render() {
        return this.getColumnContent(this.column);
    }
}

export default withTheme(ShareclassColumnValue) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassColumnValue.tsx